import { Box } from "@mui/material";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { loadNeshanLeaflet } from "../../../services/map/neshanSdkLoader";
import type {
  NeshanLeafletLayerGroup,
  NeshanLeafletMap,
  NeshanLeafletMarker,
} from "../../../services/map/neshanSdkLoader";
import { mapService } from "../../../services/map/mapService";
import { mapConfig } from "../../../services/map/mapConfig";
import MapSkeleton from "./MapSkeleton";

export interface MapMarkerData {
  id?: string | number;
  location: [number, number];
  name?: string;
  description?: string;
  color?: string;
  icon?: string;
  actionTitle?: string;
}

export interface MapPolygonData {
  positions: [number, number][];
  color?: string;
  fillColor?: string;
  fillOpacity?: number;
}

export interface NeshanMapViewHandle {
  setView: (center: [number, number], zoom: number) => void;
  getMap: () => NeshanLeafletMap | null;
}

interface NeshanMapViewProps {
  center?: [number, number];
  zoom?: number;
  height?: number | string;
  mapType?: string;
  customMapUrl?: string;
  loading?: boolean;
  readOnly?: boolean;
  markerPosition?: [number, number] | null;
  draggableMarker?: boolean;
  markers?: MapMarkerData[];
  polygons?: MapPolygonData[];
  onMapClick?: (lat: number, lng: number) => void;
  onMarkerDragEnd?: (lat: number, lng: number) => void;
  onMarkerSelect?: (id: string | number) => void;
  onMapReady?: () => void;
}

const NeshanMapView = forwardRef<NeshanMapViewHandle, NeshanMapViewProps>(
  (
    {
      center = mapService.getDefaultCenter(),
      zoom = mapService.getDefaultZoom(),
      height = 400,
      mapType,
      customMapUrl,
      loading = false,
      readOnly = false,
      markerPosition = null,
      draggableMarker = false,
      markers = [],
      polygons = [],
      onMapClick,
      onMarkerDragEnd,
      onMarkerSelect,
      onMapReady,
    },
    ref
  ) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const mapRef = useRef<NeshanLeafletMap | null>(null);
    const leafletRef = useRef<Awaited<ReturnType<typeof loadNeshanLeaflet>> | null>(null);
    const markerRef = useRef<NeshanLeafletMarker | null>(null);
    const overlayLayerRef = useRef<NeshanLeafletLayerGroup | null>(null);
    const [mapReady, setMapReady] = useState(false);
    const [initError, setInitError] = useState<string | null>(null);

    useImperativeHandle(ref, () => ({
      setView(nextCenter: [number, number], nextZoom: number) {
        mapRef.current?.setView(nextCenter, nextZoom, { animate: true });
      },
      getMap() {
        return mapRef.current;
      },
    }));

    useEffect(() => {
      let disposed = false;

      const initMap = async () => {
        if (!containerRef.current || mapRef.current) {
          return;
        }

        try {
          const clientConfig = await mapService.ensureReady();
          const apiKey = clientConfig?.apiKey?.trim() ?? "";

          if (!apiKey) {
            setInitError("کلید API نقشه نشان در تنظیمات سیستم ثبت نشده است.");
            return;
          }

          const L = await loadNeshanLeaflet();
          if (disposed || !containerRef.current) {
            return;
          }

          leafletRef.current = L;
          const tileConfig = await mapService.getTileConfig();
          const resolvedMapType =
            customMapUrl ||
            mapType ||
            tileConfig.mapType ||
            mapConfig.defaultMapType;

          const map = new L.Map(containerRef.current, {
            key: apiKey,
            maptype: resolvedMapType,
            center,
            zoom,
          });

          mapRef.current = map;
          overlayLayerRef.current = L.layerGroup().addTo(map);
          setMapReady(true);
          onMapReady?.();
        } catch {
          setInitError("بارگذاری نقشه با خطا مواجه شد.");
        }
      };

      initMap();

      return () => {
        disposed = true;
        if (mapRef.current) {
          mapRef.current.remove();
          mapRef.current = null;
        }
        markerRef.current = null;
        overlayLayerRef.current = null;
        leafletRef.current = null;
        setMapReady(false);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      if (!mapReady || !mapRef.current) {
        return;
      }
      mapRef.current.setView(center, zoom, { animate: true });
    }, [center, zoom, mapReady]);

    useEffect(() => {
      if (!mapReady || !mapRef.current || !leafletRef.current) {
        return;
      }

      const L = leafletRef.current;
      const map = mapRef.current;

      if (markerRef.current) {
        markerRef.current.remove();
        markerRef.current = null;
      }

      if (markerPosition) {
        const marker = L.marker(markerPosition, {
          draggable: !readOnly && draggableMarker,
        }).addTo(map);

        markerRef.current = marker;

        if (!readOnly && draggableMarker) {
          marker.on("dragend", () => {
            const latLng = marker.getLatLng();
            onMarkerDragEnd?.(latLng.lat, latLng.lng);
          });
        }
      }
    }, [
      draggableMarker,
      mapReady,
      markerPosition,
      onMarkerDragEnd,
      readOnly,
    ]);

    useEffect(() => {
      if (!mapReady || !mapRef.current || !leafletRef.current || !overlayLayerRef.current) {
        return;
      }

      const L = leafletRef.current;
      const overlayLayer = overlayLayerRef.current;
      overlayLayer.clearLayers();

      markers.forEach((item, index) => {
        const icon = item.icon
          ? new L.Icon({
              iconUrl: item.icon,
              iconSize: new L.Point(28, 28),
            })
          : undefined;

        const marker = L.marker(
          item.location,
          icon ? { icon } : undefined
        ) as NeshanLeafletMarker;

        const popupLines = [item.name, item.description].filter(Boolean);
        let popupHtml = popupLines.join("<br/>");

        if (item.actionTitle && onMarkerSelect && item.id != null) {
          popupHtml += `<br/><button type="button" data-marker-id="${item.id}" style="margin-top:8px;padding:4px 10px;cursor:pointer;">${item.actionTitle}</button>`;
        }

        if (popupHtml) {
          marker.bindPopup(popupHtml);
          marker.on("popupopen", () => {
            const popupElement = marker.getPopup()?.getElement();
            const button = popupElement?.querySelector("button[data-marker-id]");
            if (button && item.id != null && onMarkerSelect) {
              button.addEventListener("click", () =>
                onMarkerSelect(item.id as string | number)
              );
            }
          });
        }

        if (overlayLayer.addLayer) {
          overlayLayer.addLayer(marker);
        } else {
          marker.addTo(mapRef.current as NeshanLeafletMap);
        }
      });

      polygons.forEach((polygon) => {
        const polygonLayer = L.polygon(polygon.positions, {
          color: polygon.color,
          fillColor: polygon.fillColor || polygon.color,
          fillOpacity: polygon.fillOpacity ?? 0.35,
        });

        if (overlayLayer.addLayer) {
          overlayLayer.addLayer(polygonLayer);
        }
      });
    }, [mapReady, markers, onMarkerSelect, polygons]);

    useEffect(() => {
      if (!mapReady || !mapRef.current || readOnly || !onMapClick) {
        return;
      }

      const map = mapRef.current;
      const handleClick = (event: { latlng: { lat: number; lng: number } }) => {
        onMapClick(event.latlng.lat, event.latlng.lng);
      };

      map.on("click", handleClick);
      return () => {
        map.off("click", handleClick);
      };
    }, [mapReady, onMapClick, readOnly]);

    if (initError) {
      return (
        <Box
          sx={{
            width: "100%",
            height,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: "1px dashed",
            borderColor: "warning.main",
            borderRadius: 1,
            color: "warning.main",
            px: 2,
            textAlign: "center",
          }}
        >
          {initError}
        </Box>
      );
    }

    return (
      <Box sx={{ position: "relative", width: "100%", height }}>
        {(loading || !mapReady) && (
          <Box sx={{ position: "absolute", inset: 0, zIndex: 2 }}>
            <MapSkeleton height={height} />
          </Box>
        )}
        <Box
          ref={containerRef}
          sx={{
            width: "100%",
            height,
            borderRadius: 1,
            overflow: "hidden",
            opacity: mapReady ? 1 : 0,
            transition: "opacity 0.25s ease",
          }}
        />
      </Box>
    );
  }
);

NeshanMapView.displayName = "NeshanMapView";

export default NeshanMapView;
